import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowTopRightOnSquareIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  HeartIcon,
  LockClosedIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid'
import FolderSelect from './FolderSelect'
import TagInput from './TagInput'
import LinkClickSparkline from './LinkClickSparkline'
import LinkDetailQr from './LinkDetailQr'
import type { BookmarkedUrl, Tag } from '../../../../types/bookmark'
import type { LinkFeatures, LinkWeeklyClicks } from '../../hooks/useLinkDetail'

const MIN_PASSWORD_LENGTH = 4

export interface LinkDetailPanelProps {
  url: BookmarkedUrl
  features: LinkFeatures
  weekly?: LinkWeeklyClicks
  isWeeklyError?: boolean
  isSaving: boolean
  onClose: () => void
  onSave: (input: Record<string, unknown>) => void
  onDelete: (urlId: string) => void
  onToggleFavorite: (urlId: string) => void
  onCopySuccess: (text: string) => void
}

/** 'https://shorty.kr/abc' 에서 앞의 scheme 만 떼어 낸다 */
function displayShortUrl(shortUrl: string): string {
  return shortUrl.replace(/^https?:\/\//, '')
}

/** 원본 주소의 도메인. 파싱에 실패하면 주소를 그대로 쓴다. */
function displayDomain(originalUrl: string): string {
  try {
    return new URL(originalUrl).hostname.replace(/^www\./, '')
  } catch {
    return originalUrl
  }
}

/** ISO 문자열을 <input type="date"> 가 읽는 'YYYY-MM-DD' 로 */
function toDateInputValue(iso?: string): string {
  if (!iso) {
    return ''
  }
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10)
}

/**
 * 링크 상세 패널.
 *
 * 목록 옆에서 바로 고치는 흐름이라 필드마다 즉시 저장한다.
 * 저장은 상위에서 하고(onSave), 여기서는 무엇이 바뀌었는지만 알린다.
 */
export default function LinkDetailPanel({
  url,
  features,
  weekly,
  isWeeklyError = false,
  isSaving,
  onClose,
  onSave,
  onDelete,
  onToggleFavorite,
  onCopySuccess,
}: LinkDetailPanelProps) {
  const { t, i18n } = useTranslation()
  const [title, setTitle] = useState(url.title ?? '')
  const [isEditingPassword, setIsEditingPassword] = useState(false)
  const [password, setPassword] = useState('')
  const passwordRef = useRef<HTMLInputElement>(null)

  // 다른 링크로 바뀌면 편집 중이던 값은 버린다
  useEffect(() => {
    setTitle(url.title ?? '')
    setIsEditingPassword(false)
    setPassword('')
  }, [url._id, url.title])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const shortUrl = url.shortUrl
  const totalClicks = weekly?.totalClicks ?? url.clickCount
  const weekClicks = weekly?.weekClicks
  const points = useMemo(() => weekly?.points ?? [], [weekly])
  const hasPassword = url.hasPassword ?? url.security?.hasPassword ?? false

  const handleTitleBlur = useCallback(() => {
    const next = title.trim()
    if (next !== (url.title ?? '')) {
      onSave({ title: next })
    }
  }, [title, url.title, onSave])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shortUrl)
      onCopySuccess(shortUrl)
    } catch {
      // 클립보드를 막아 둔 브라우저에서는 조용히 넘어간다
    }
  }, [shortUrl, onCopySuccess])

  const handleTagsChange = useCallback(
    (tags: Tag[]) => onSave({ tags: tags.map(tag => tag.name) }),
    [onSave]
  )

  const handleExpiryChange = useCallback(
    (value: string) => {
      if (!value) {
        onSave({ expiresAt: null })
        return
      }
      const parsed = new Date(`${value}T00:00:00`)
      if (!Number.isNaN(parsed.getTime())) {
        onSave({ expiresAt: parsed.toISOString() })
      }
    },
    [onSave]
  )

  const handlePasswordSubmit = useCallback(() => {
    if (password.length >= MIN_PASSWORD_LENGTH) {
      onSave({ password })
      setPassword('')
      setIsEditingPassword(false)
    }
  }, [password, onSave])

  const expiryLocked = !features.expirationDates
  const passwordLocked = !features.passwordProtection

  return (
    <aside
      role="complementary"
      aria-label={t('bookmark.detail.panelTitle')}
      className="flex h-full w-full flex-col border-l border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
    >
      <header className="flex items-center gap-1 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <span className="flex-1 text-sm font-semibold text-gray-500 dark:text-gray-400">
          {t('bookmark.detail.panelTitle')}
        </span>
        <button
          type="button"
          aria-label={t('bookmark.detail.favorite')}
          aria-pressed={url.isFavorite}
          onClick={() => onToggleFavorite(url._id)}
          className="rounded-lg p-2 text-gray-400 transition hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-gray-700"
        >
          {url.isFavorite ? (
            <HeartSolidIcon className="h-5 w-5 text-rose-500" />
          ) : (
            <HeartIcon className="h-5 w-5" />
          )}
        </button>
        <a
          href={url.originalUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('bookmark.detail.openOriginal')}
          className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
        >
          <ArrowTopRightOnSquareIcon className="h-5 w-5" />
        </a>
        <button
          type="button"
          aria-label={t('bookmark.detail.close')}
          onClick={onClose}
          className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </header>

      <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-5">
        <div className="flex flex-col gap-1">
          <label className="sr-only" htmlFor="link-detail-title">
            {t('bookmark.detail.titleLabel')}
          </label>
          <input
            id="link-detail-title"
            value={title}
            disabled={isSaving}
            onChange={event => setTitle(event.target.value)}
            onBlur={handleTitleBlur}
            placeholder={t('bookmark.detail.titlePlaceholder')}
            className="-mx-2 rounded-lg border border-transparent bg-transparent px-2 py-1 text-xl font-extrabold tracking-tight text-gray-900 transition hover:border-gray-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:text-white dark:hover:border-gray-600"
          />
          <span className="break-all text-sm text-gray-400">
            {displayDomain(url.originalUrl)}
          </span>
        </div>

        <div className="flex items-stretch gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex items-center gap-2 rounded-xl border border-primary-100 bg-primary-50 py-1.5 pl-3 pr-1.5 dark:border-primary-900 dark:bg-primary-950">
              <span className="min-w-0 flex-1 truncate font-mono text-sm font-bold text-primary-700 dark:text-primary-300">
                {displayShortUrl(shortUrl)}
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-primary-700"
              >
                {t('bookmark.detail.copy')}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-0.5 rounded-xl border border-gray-100 px-3 py-2 dark:border-gray-700">
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  {t('bookmark.detail.totalClicks')}
                </span>
                <span className="text-xl font-extrabold tabular-nums text-gray-900 dark:text-white">
                  {totalClicks}
                </span>
              </div>
              <div className="flex flex-col gap-0.5 rounded-xl border border-gray-100 px-3 py-2 dark:border-gray-700">
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  {t('bookmark.detail.last7Days')}
                </span>
                <span className="text-xl font-extrabold tabular-nums text-gray-900 dark:text-white">
                  {weekClicks ?? '—'}
                </span>
              </div>
            </div>
          </div>

          <LinkDetailQr shortUrl={shortUrl} shortCode={url.shortCode} />
        </div>

        <section className="flex flex-col gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-bold text-gray-900 dark:text-white">
            <ArrowTrendingUpIcon className="h-4 w-4 text-gray-400" />
            {t('bookmark.detail.weeklyChart')}
          </h3>
          {isWeeklyError ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('bookmark.detail.chartError')}
            </p>
          ) : (
            <LinkClickSparkline
              points={points}
              language={i18n.resolvedLanguage ?? i18n.language}
            />
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">
            {t('bookmark.detail.folder')}
          </h3>
          <FolderSelect
            value={url.folderId?._id ?? null}
            disabled={isSaving}
            placeholder={t('bookmark.detail.noFolder')}
            onChange={folderId => onSave({ folderId })}
          />
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">
            {t('bookmark.detail.tags')}
          </h3>
          <TagInput
            value={url.tags}
            disabled={isSaving}
            onChange={handleTagsChange}
          />
        </section>

        <section className="flex flex-col gap-1">
          <h3 className="mb-1 text-sm font-bold text-gray-900 dark:text-white">
            {t('bookmark.detail.settings')}
          </h3>

          <div className="flex flex-col gap-2 border-b border-gray-100 py-2.5 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <LockClosedIcon className="h-5 w-5 text-gray-400" />
              <span className="flex-1 text-sm text-gray-900 dark:text-white">
                {t('bookmark.detail.passwordProtection')}
              </span>
              {hasPassword ? (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => onSave({ password: null })}
                  className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300"
                >
                  {t('bookmark.detail.passwordClear')}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={passwordLocked || isSaving}
                  onClick={() => {
                    setIsEditingPassword(true)
                    requestAnimationFrame(() => passwordRef.current?.focus())
                  }}
                  className="rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300"
                >
                  {t('bookmark.detail.passwordSet')}
                </button>
              )}
            </div>
            {isEditingPassword && (
              <input
                ref={passwordRef}
                type="password"
                aria-label={t('bookmark.detail.passwordProtection')}
                value={password}
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={50}
                placeholder={t('bookmark.detail.passwordPlaceholder')}
                onChange={event => setPassword(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    handlePasswordSubmit()
                  }
                }}
                onBlur={handlePasswordSubmit}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            )}
            {passwordLocked && (
              <p className="text-xs text-gray-400">
                {t('bookmark.detail.planRequired')}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2 py-2.5">
            <div className="flex items-center gap-3">
              <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
              <label
                htmlFor="link-detail-expires"
                className="flex-1 text-sm text-gray-900 dark:text-white"
              >
                {t('bookmark.detail.expiresAt')}
              </label>
              <input
                id="link-detail-expires"
                type="date"
                disabled={expiryLocked || isSaving}
                defaultValue={toDateInputValue(
                  url.expiresAt ?? url.security?.expiresAt
                )}
                onChange={event => handleExpiryChange(event.target.value)}
                className="rounded-lg border border-gray-300 px-2 py-1 text-sm disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            {expiryLocked && (
              <p className="text-xs text-gray-400">
                {t('bookmark.detail.planRequired')}
              </p>
            )}
          </div>
        </section>
      </div>

      <footer className="flex gap-2 border-t border-gray-100 px-5 py-3 dark:border-gray-700">
        <button
          type="button"
          onClick={() => onDelete(url._id)}
          className="flex items-center gap-1.5 rounded-xl border border-red-200 px-3.5 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-950"
        >
          <TrashIcon className="h-4 w-4" />
          {t('common.delete')}
        </button>
        <Link
          to={`/analytics/${url.shortCode}`}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-gray-900 py-2 text-sm font-semibold text-white transition hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600"
        >
          <ChartBarIcon className="h-4 w-4" />
          {t('bookmark.detail.viewAnalytics')}
        </Link>
      </footer>
    </aside>
  )
}
